import { sanitizeHTML } from './sanitization.utils';
import { assertNever } from './typescript.utils';

export function normalizeText(value: string, type: NormalizeTextType, options: NormalizeTextToPlainOptions & NormalizeTextToRichOptions = {}) {
  if (type === NormalizeTextType.Plain) {
    return normalizeTextToPlain(value, options);
  } else if (type === NormalizeTextType.Rich) {
    return normalizeTextToRich(value, options);
  }
}

export function normalizeTextToPlain(value: string, options: NormalizeTextToPlainOptions = {}) {
  if (!value) {
    return '';
  }

  tmpElem.innerHTML = sanitizeHTML(value);

  function walkValueTree(nodes: NodeListOf<ChildNode>) {
    const result = [];

    nodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const nodeName = node.nodeName.toLowerCase();

        if (BlockElements.includes(nodeName)) {
          if (node.childNodes.length) {
            result.push(walkValueTree(node.childNodes));
          }
        } else if (nodeName === 'br' && node.nextSibling) {
          result.push('\n');
        } else if (InlineTextElements.includes(nodeName)) {
          result.push(node.textContent);

          if ((nodeName === 'td' || nodeName === 'th') && node.nextSibling) {
            result.push(' ');
          }
        } else if (options.preserveMarkers && nodeName === MarkerElement) {
          result.push(cleanMarkerElement(node as Element).outerHTML);
        } else if (options.preserveMarkersText && nodeName === MarkerElement) {
          result.push((node as Element).textContent);
        } else if (options.preserveImages && nodeName === 'img') {
          result.push('▨');
        }
      } else if (node.nodeType === Node.TEXT_NODE) {
        result.push(node.textContent);
      }
    });

    return result;
  }

  function walkBlockTree(nodes: any[]) {
    let result = '';

    nodes.forEach((node, index) => {
      const prevNode = nodes[index - 1];

      if (prevNode && (Array.isArray(prevNode) || Array.isArray(node))) {
        result += '\n';
      }

      if (Array.isArray(node)) {
        result += walkBlockTree(node);
      } else {
        result += node;
      }
    });

    return result;
  }

  const blockTree = walkValueTree(tmpElem.childNodes);

  return walkBlockTree(blockTree);
}

const RECOGNIZE_LINKS_REGEXP = '([^\\S]|^)(((https?\\:\\/\\/)|(www\\.))(\\S+))';

function hasTextLinks(text: string) {
  return new RegExp(RECOGNIZE_LINKS_REGEXP, 'i').test(text);
}

// Text must be a encoded html text string, not decoded!!! (e.g. html entities are still unescaped)
// Use for example `encodeToHTMLText` function.
export function recognizeLinksInHTMLText(text: string, options: { sanitize: boolean; } = { sanitize: true }): Node[] {
  const htmlText = text.replace(new RegExp(RECOGNIZE_LINKS_REGEXP, 'gi'), (match, space, url) => {
    let normalizedUrl = url;

    if (!normalizedUrl.match('^https?://')) {
      normalizedUrl = 'http://' + normalizedUrl;
    }

    const linkHtml = `<a href="${normalizedUrl}" target="_blank">${url}</a>`;
    return [
      space,
      options.sanitize
        ? sanitizeHTML(linkHtml)
        : linkHtml
    ].join('');
  });

  const doc = new DOMParser().parseFromString(htmlText, 'text/html');
  return [...doc.body.childNodes];
}

// Text must be a encoded html text string, not decoded!!! (e.g. html entities are still unescaped)
// Use for example `encodeToHTMLText` function.
export function recognizeLinks(text: string, options: { sanitize: boolean; } = { sanitize: true }): string {
  return serializeNodes(recognizeLinksInHTMLText(text, options));
}

function createBrNode() {
  return document.createElement('br');
}

function createTextNode(text: string) {
  return document.createTextNode(text);
}

function createParagraphWithNodes(nodes: Node[]) {
  const p = document.createElement('p');
  nodes.forEach(node => {
    p.appendChild(node);
  });
  return p;
}

// `decodedText` is in the form where html entities are decoded to chars
// e.g. `&lt;` is literaly as `<` in text.
function encodeToHTMLText(decodedText: string) {
  return createParagraphWithNodes([createTextNode(decodedText)]).innerHTML;
}

export function serializeNodes(nodes: Node[]): string {
  return createParagraphWithNodes(nodes).innerHTML;
}

export function normalizeTextToRich(value: string, options: NormalizeTextToRichOptions = {}) {
  if (!value) {
    return '<p><br></p>';
  }

  tmpElem.innerHTML = sanitizeHTML(value);

  tmpElem.querySelectorAll(MarkerElement).forEach(markerElem => {
    const nextMarkerElem = cleanMarkerElement(markerElem);

    markerElem.parentNode.replaceChild(nextMarkerElem, markerElem);
  });

  type ResultNodeType = 'text'
    | 'emptyText'
    | 'textWithMarkers'
    | 'textWithLinks'
    | 'marker'
    | 'element';
  interface ResultNode {
    type: ResultNodeType;
    nodes: Node[];
  }

  function getPreviousResultNode(nodes: ResultNode[]) {
    return nodes.length
      ? nodes[nodes.length - 1]
      : null;
  }

  function isResultNodeFromTypes(node: ResultNode, types: ResultNodeType[]) {
    return node && types.includes(node.type);
  }

  const resultNodes: ResultNode[] = [];
  tmpElem.childNodes.forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const nodeName = node.nodeName.toLowerCase();

      if (nodeName === MarkerElement) {
        resultNodes.push({ type: 'marker', nodes: [node] });
      } else {
        resultNodes.push({ type: 'element', nodes: [node] });
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const textLines = node.textContent.split('\n');
      const lineNodes: ResultNode[] = textLines.map((textLine) => {
        if (textLine) {
          return options.recognizeLinks && hasTextLinks(textLine)
            ? { type: 'textWithLinks', nodes: recognizeLinksInHTMLText(encodeToHTMLText(textLine)) }
            : { type: 'text', nodes: [createTextNode(textLine)] };
        } else {
          // Special case for handling empty lines. Because of Quill we
          // are inserting `<br>` inside of `<p></p>`
          return { type: 'emptyText', nodes: [createBrNode()] };
        }
      });
      resultNodes.push(...lineNodes);
    }
  });

  // Compress result nodes to fewer lines
  let lastNodeType: ResultNodeType = null;
  const compressedLines = resultNodes.reduce((acc, node) => {
    let result = acc;

    switch (node.type) {
      case 'marker': {
        const previousResultNode = getPreviousResultNode(acc);
        if (isResultNodeFromTypes(previousResultNode, ['text', 'textWithMarkers'])) {
          previousResultNode.nodes.push(...node.nodes);
          previousResultNode.type = 'textWithMarkers';
          break;
        } else {
          result = [...acc, node];
          break;
        }
      }
      case 'text': {
        const previousResultNode = getPreviousResultNode(acc);
        if (isResultNodeFromTypes(previousResultNode, ['textWithMarkers', 'marker']) && lastNodeType !== 'text') {
          previousResultNode.nodes.push(...node.nodes);
          previousResultNode.type = 'textWithMarkers';
          break;
        }
        else {
          result = [...acc, node];
          break;
        }
      }
      case 'emptyText':
      case 'element':
      case 'textWithLinks':
      case 'textWithMarkers':
        result = [...acc, node];
        break;
      default:
        assertNever(node.type);
    }

    lastNodeType = node.type;
    return result;
  }, [] as ResultNode[]);

  // Generate output lines
  return compressedLines.map(node => {
    switch (node.type) {
      case 'element':
        return (node.nodes[0] as HTMLElement).outerHTML;
      default:
        return createParagraphWithNodes(node.nodes).outerHTML;
    }
  }).join('');
}

export function isTextEmpty(text: string) {
  return !text || text === '<p><br></p>';
}

function cleanMarkerElement(markerElem: Element): HTMLElement {
  const tmpMarkerElem = document.createElement(MarkerElement);

  MarkerElementAttributes.forEach(attr => {
    const attrValue = markerElem.getAttribute(attr);

    if (attrValue) {
      tmpMarkerElem.setAttribute(attr, attrValue);
    }
  });

  return tmpMarkerElem;
}

export enum NormalizeTextType {
  Plain = 'Plain',
  Rich = 'Rich'
}

export interface NormalizeTextToPlainOptions {
  preserveImages?: boolean;
  preserveMarkers?: boolean;
  preserveMarkersText?: boolean;
}

export interface NormalizeTextToRichOptions {
  recognizeLinks?: boolean;
}

const tmpElem = document.createElement('div');

// Source: https://github.com/apostrophecms/sanitize-html
const BlockElements = ['address', 'article', 'aside', 'blockquote', 'dd', 'div', 'dl', 'dt', 'figcaption',
  'figure', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hgroup', 'hr', 'li', 'main', 'nav',
  'ol', 'p', 'pre', 'section', 'table', 'tbody', 'thead', 'tfoot', 'tr', 'ul'];

const InlineTextElements = ['a', 'abbr', 'b', 'bdi', 'bdo', 'cite', 'code', 'data', 'dfn', 'em', 'i', 'kbd',
  'mark', 'q', 'rb', 'rp', 'rt', 'rtc', 'ruby', 's', 'samp', 'small', 'span', 'strong', 'sub', 'sup', 'td',
  'th', 'time', 'u', 'var', 'wbr'];

const MarkerElement = 'ppl-tag';

const MarkerElementAttributes = ['data-id', 'data-type', 'data-offset', 'data-offset-unit', 'data-offset-seconds', 'data-relation-type', 'data-relation-lookup-field-id', 'data-transform', 'data-settings'];
